/**
 * BulkLinkTrimDialog — link one trim/component SKU to many parent products in a
 * single pass (BLU-534, the "neck label used everywhere" flow).
 *
 * Canonical, reusable dialog for the bulk-link action. Server-searched product
 * picker (so it scales past a big catalog), qty/UOM/deduct-at parameters shared
 * with the BOM card, and honest handling of already-linked products (excluded
 * up-front, and any server-side `skipped` surfaced in the toast).
 *
 * Design: inventory-app language (DESIGN.md) — shadcn Dialog, one brand accent,
 * every state (loading / empty / filtered-empty / error) designed, footnote for
 * the variant-coverage caveat. Used from the Products page Trims tab; the BOM
 * card's inline bulk-link dialog should migrate onto this component.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { keepPreviousData } from '@tanstack/react-query';
import { UNIT_OF_MEASURE_VALUES } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Uom = (typeof UNIT_OF_MEASURE_VALUES)[number];

// Mirrors BomCard's copy so the two surfaces read identically. Labels are UI;
// the values come from the shared deductAtSchema.
const DEDUCT_AT_OPTIONS = [
  { value: 'order', label: 'Order fulfilment' },
  { value: 'receipt', label: 'PO receipt' },
] as const;
type DeductAt = (typeof DEDUCT_AT_OPTIONS)[number]['value'];

const PICKER_PAGE_SIZE = 100;

export interface BulkLinkTrimDialogProps {
  open: boolean;
  onClose: () => void;
  /** The trim/component SKU row id being linked. */
  componentSkuId: string;
  /** The trim's SKU code (rendered mono in the header). */
  componentSku: string;
  /** The trim's title, if any (shown beside the code). */
  componentTitle?: string | null;
  /** Optional parent product to hide from the picker (e.g. the current one). */
  excludeProductId?: string;
  /** Fired after a successful link so callers can refresh dependent views. */
  onLinked?: () => void;
}

export default function BulkLinkTrimDialog({
  open,
  onClose,
  componentSkuId,
  componentSku,
  componentTitle,
  excludeProductId,
  onLinked,
}: BulkLinkTrimDialogProps) {
  const utils = trpc.useUtils();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [qty, setQty] = useState('1');
  const [uom, setUom] = useState<Uom>('pcs');
  const [deductAt, setDeductAt] = useState<DeductAt>('order');

  // Reset transient state each time the dialog opens for a (possibly) new trim.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setDebouncedSearch('');
    setSelected(new Set());
    setQty('1');
    setUom('pcs');
    setDeductAt('order');
  }, [open, componentSkuId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Server-searched product picker (POD + PAP; the backend never returns trims
  // as products). Paginated so it scales past a large catalog.
  const productsQuery = trpc.inventory.listProductsGrouped.useQuery(
    { search: debouncedSearch || undefined, page: 1, pageSize: PICKER_PAGE_SIZE },
    { enabled: open, placeholderData: keepPreviousData },
  );

  // Products this trim is already linked to — excluded so the operator can't
  // double-link (and the count feeds honest select-all behaviour).
  const linkedQuery = trpc.skuComponents.list.useQuery(
    { componentSkuId },
    { enabled: open, staleTime: 10_000 },
  );

  const linkedProductIds = useMemo(
    () =>
      new Set(
        (linkedQuery.data ?? [])
          .map((c) => c.parentProductId)
          .filter((id): id is string => !!id),
      ),
    [linkedQuery.data],
  );

  const allProducts = productsQuery.data?.products ?? [];
  const totalProducts = productsQuery.data?.totalProducts ?? 0;
  const truncated = totalProducts > allProducts.length;

  // Rows the operator can actually pick (exclude the current product, if any).
  const rows = useMemo(
    () => allProducts.filter((p) => p.id !== excludeProductId),
    [allProducts, excludeProductId],
  );
  const linkableIds = useMemo(
    () => rows.filter((p) => !linkedProductIds.has(p.id)).map((p) => p.id),
    [rows, linkedProductIds],
  );

  const allLinkableSelected =
    linkableIds.length > 0 && linkableIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (linkableIds.every((id) => prev.has(id))) {
        // Deselect only the currently-visible linkable rows; leave any
        // off-screen selections from a prior search intact.
        const next = new Set(prev);
        for (const id of linkableIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of linkableIds) next.add(id);
      return next;
    });
  };

  const bulkLinkMut = trpc.skuComponents.bulkLink.useMutation({
    onSuccess: (r) => {
      const base = `Linked to ${r.linked} product${r.linked === 1 ? '' : 's'}`;
      const withSkipped =
        r.skipped > 0 ? `${base} (${r.skipped} already linked, skipped)` : base;
      toast.success(withSkipped);
      utils.skuComponents.list.invalidate();
      onLinked?.();
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const qtyNum = Number(qty);
  // The API caps one batch at 500 parents; cross-search select-all can
  // accumulate past it, so guard here with a friendly message instead of
  // letting the backend zod reject surface raw (review finding).
  const MAX_BATCH = 500;
  const overCap = selected.size > MAX_BATCH;
  const canSubmit =
    selected.size > 0 && !overCap && Number.isFinite(qtyNum) && qtyNum > 0 && !bulkLinkMut.isPending;

  const submit = () => {
    if (!canSubmit) return;
    bulkLinkMut.mutate({
      componentSkuId,
      parentProductIds: Array.from(selected),
      quantityPerUnit: qtyNum,
      unitOfMeasure: uom,
      deductAt,
    });
  };

  const isLoading = productsQuery.isLoading || linkedQuery.isLoading;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-x-2">
            <span>Link</span>
            <span className="font-mono">{componentSku}</span>
            <span>to products</span>
          </DialogTitle>
          <DialogDescription>
            {componentTitle ? `${componentTitle}. ` : ''}
            Each selected product gets this trim in its bill of materials. Products it is
            already on are shown but cannot be re-selected.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Input
              placeholder="Search products by name or SKU"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-xs"
              aria-label="Search products"
            />
            {productsQuery.isFetching && !productsQuery.isLoading && (
              <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Select-all toolbar — mirrors the products list all-rows checkbox. */}
          <div className="flex items-center justify-between px-1">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={
                  allLinkableSelected ? true : someSelected ? 'indeterminate' : false
                }
                onCheckedChange={toggleSelectAll}
                disabled={linkableIds.length === 0}
                aria-label="Select all products in view"
              />
              Select all in view
            </label>
            <span
              className={
                overCap
                  ? 'text-xs tabular-nums font-medium text-destructive'
                  : 'text-xs tabular-nums text-muted-foreground'
              }
            >
              {selected.size} selected
              {overCap ? ` (max ${MAX_BATCH} per batch, deselect ${selected.size - MAX_BATCH})` : ''}
            </span>
          </div>

          <div className="max-h-64 overflow-auto rounded-md border p-1">
            {isLoading ? (
              <p className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading products…
              </p>
            ) : productsQuery.error ? (
              <div className="px-2 py-3 text-xs">
                <p className="text-destructive">Could not load products.</p>
                <button
                  type="button"
                  className="mt-1 text-muted-foreground underline hover:text-foreground"
                  onClick={() => productsQuery.refetch()}
                >
                  Retry
                </button>
              </div>
            ) : rows.length === 0 ? (
              <p className="px-2 py-3 text-xs text-muted-foreground">
                {debouncedSearch
                  ? `No products match "${debouncedSearch}".`
                  : 'No products found.'}
              </p>
            ) : (
              rows.map((p) => {
                const alreadyLinked = linkedProductIds.has(p.id);
                return (
                  <label
                    key={p.id}
                    className={`flex items-center gap-2 rounded px-2 py-1.5 ${
                      alreadyLinked
                        ? 'cursor-default opacity-60'
                        : 'cursor-pointer hover:bg-muted/40'
                    }`}
                  >
                    <Checkbox
                      checked={alreadyLinked ? true : selected.has(p.id)}
                      disabled={alreadyLinked}
                      onCheckedChange={() => !alreadyLinked && toggle(p.id)}
                      aria-label={`Select ${p.title ?? 'product'}`}
                    />
                    <span className="min-w-0 flex-1 truncate text-xs">
                      {p.title || 'Untitled product'}
                    </span>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {p.productionType}
                    </Badge>
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                      {p.variantCount} variant{p.variantCount === 1 ? '' : 's'}
                    </span>
                    {alreadyLinked && (
                      <span className="shrink-0 text-[10px] font-medium text-muted-foreground">
                        Already linked
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>

          {truncated && (
            <p className="px-1 text-[11px] text-muted-foreground">
              Showing the first {allProducts.length} of {totalProducts}. Narrow your search to
              see more.
            </p>
          )}

          {/* Link parameters — shared with the BOM card. */}
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <div className="w-24 space-y-1">
              <span className="text-xs font-medium">Qty / unit</span>
              <Input
                type="number"
                min="0.0001"
                step="any"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="h-8 text-xs"
                aria-label="Quantity per unit"
              />
            </div>
            <div className="w-20 space-y-1">
              <span className="text-xs font-medium">UOM</span>
              <Select value={uom} onValueChange={(v) => setUom(v as Uom)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_OF_MEASURE_VALUES.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40 space-y-1">
              <span className="text-xs font-medium">Deducts at</span>
              <Select value={deductAt} onValueChange={(v) => setDeductAt(v as DeductAt)}>
                <SelectTrigger
                  className="h-8 text-xs"
                  title="Receipt = the factory consumed it before goods arrived."
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEDUCT_AT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />
            Linking to a product covers all its variant SKUs.
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={bulkLinkMut.isPending}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={!canSubmit}>
            {bulkLinkMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Link{selected.size > 0 ? ` ${selected.size} ` : ' '}product
            {selected.size === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
